import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { usePickemApi } from '~/services/api'
import type {
  Tournament,
  RewardLevel,
  RewardsResponse,
  ClaimResponse
} from '../types'
import { generateIdempotencyKey } from '../composables/use-idempotency-key'
import { usePickemAuth } from '../composables/use-pickem-auth'

// Хранилище наград турнира.
// Источник правды: docs/pickem/docs/technical/03-api-basic.md → Shape: Rewards.
//
// Что мы тут делаем:
// 1. fetch(tournamentId) — забираем shape `{ user_xp, levels[] }`.
// 2. claim(levelId) — мутирующий запрос с X-Idempotency-Key (на серию ретраев
//    один и тот же ключ; на новое логическое нажатие — новый, как и в swiss/bracket).
// 3. Пополиваем GET'ом, если в списке есть available-уровни — флаг `can_claim`
//    может смениться на false за минуту до окончания турнира (см. документ).
//    Останавливаем поллинг, как только все available забраны или окно auto-claim
//    миновало.
//
// Идемпотентность: для каждого level_id храним «активный» ключ. Сбрасываем
// после успешного 200-ответа. На 422/423 — оставляем (пользователь может
// перенажать; сервер вернёт тот же закэшированный ответ).

const POLL_INTERVAL_MS = 30_000

export const useRewardsStore = defineStore('pickem-rewards', () => {
  const pickemApi = usePickemApi()
  const { ensureAuthorized } = usePickemAuth()

  const data = ref<RewardsResponse | null>(null)
  const tournamentId = ref<Tournament['id'] | null>(null)
  const isLoading = ref(false)
  const claimingLevelIds = ref<Set<number>>(new Set())
  const idempotencyMap = new Map<number, string>()

  let pollTimer: ReturnType<typeof setTimeout> | null = null

  const userXp = computed(() => data.value?.user_xp ?? 0)
  const levels = computed<RewardLevel[]>(() => data.value?.levels ?? [])

  // «Текущий» уровень (последний achieved). Алгоритм по доке:
  // - levels отсортированы по position.
  // - achieved = max points_required ≤ user_xp.
  const achievedLevel = computed<RewardLevel | null>(() => {
    let best: RewardLevel | null = null
    for (const level of levels.value) {
      if (userXp.value >= level.points_required) {
        if (!best || level.position > best.position) best = level
      }
    }
    return best
  })

  // Следующий уровень (для XP-бара: видно «осталось столько-то»).
  const nextLevel = computed<RewardLevel | null>(() => {
    let best: RewardLevel | null = null
    for (const level of levels.value) {
      if (userXp.value < level.points_required) {
        if (!best || level.position < best.position) best = level
      }
    }
    return best
  })

  // Прогресс юзера от prev до next, [0..1]. Для XP-бара.
  const progress = computed(() => {
    const next = nextLevel.value
    const prev = achievedLevel.value
    if (!next) return 1
    const lower = prev?.points_required ?? 0
    const upper = next.points_required
    if (upper <= lower) return 0
    return Math.min(1, Math.max(0, (userXp.value - lower) / (upper - lower)))
  })

  const hasClaimable = computed(() =>
    levels.value.some(l => l.status === 'available')
  )

  const isClaiming = (levelId: number) => claimingLevelIds.value.has(levelId)

  const fetchRewards = async (id: Tournament['id']) => {
    tournamentId.value = id
    isLoading.value = true
    try {
      data.value = await pickemApi.getRewards(id)
      return data.value
    } finally {
      isLoading.value = false
    }
  }

  const claim = async (levelId: number): Promise<ClaimResponse | null> => {
    if (!tournamentId.value) return null

    const level = levels.value.find(item => item.id === levelId)
    if (!level) return null

    let key = idempotencyMap.get(levelId)
    if (!key) {
      key = generateIdempotencyKey()
      idempotencyMap.set(levelId, key)
    }

    try {
      await ensureAuthorized()
    } catch {
      return null
    }

    const next = new Set(claimingLevelIds.value)
    next.add(levelId)
    claimingLevelIds.value = next

    try {
      const response = await pickemApi.claimReward(levelId, key)

      // Локально проставим status='claimed' на затронутом уровне, чтобы UI
      // отреагировал сразу. Заодно перезапросим всё, чтобы синхронизировать
      // прочие нюансы (например, can_claim других уровней).
      if (data.value) {
        data.value = {
          ...data.value,
          levels: data.value.levels.map(l =>
            l.id === levelId ? { ...l, status: 'claimed', can_claim: false } : l
          )
        }
      }

      idempotencyMap.delete(levelId)
      // Тихая фоновая синхронизация (не ждём). Мутации других уровней
      // изменить не должны, но обновим user_xp и прочую служебку.
      fetchRewards(tournamentId.value).catch(() => undefined)

      return response
    } finally {
      const after = new Set(claimingLevelIds.value)
      after.delete(levelId)
      claimingLevelIds.value = after
    }
  }

  // Поллим, пока есть available уровни. Это нужно, чтобы фронт корректно
  // переключал can_claim → false за 1 минуту до конца турнира (см.
  // 03-api-basic.md: «can_claim=false если до tournament.end_at < 60s»).
  const startPolling = () => {
    stopPolling()
    if (!tournamentId.value) return
    if (!hasClaimable.value) return

    pollTimer = setTimeout(async () => {
      if (!tournamentId.value) return
      try {
        await fetchRewards(tournamentId.value)
      } catch {
        // Молчим — при следующем интервале попробуем ещё раз.
      }
      // Перезапуск только если осталось что забирать.
      if (hasClaimable.value) startPolling()
    }, POLL_INTERVAL_MS)
  }

  const stopPolling = () => {
    if (pollTimer) {
      clearTimeout(pollTimer)
      pollTimer = null
    }
  }

  const reset = () => {
    stopPolling()
    data.value = null
    tournamentId.value = null
    claimingLevelIds.value = new Set()
    idempotencyMap.clear()
  }

  return {
    data,
    levels,
    userXp,
    achievedLevel,
    nextLevel,
    progress,
    hasClaimable,
    isLoading,
    fetchRewards,
    claim,
    isClaiming,
    startPolling,
    stopPolling,
    reset
  }
})
